import React, { useEffect, useRef, useState } from 'react';
import './MobileAppLanding.css';
import Toolbar from '../toolbar/ToolBar';
import TopFooter from '../top-footer/TopFooter';
import BottomFooter from '../bottom-footer/BottomFooter';

const MobileAppLanding = ({ toggleTheme, theme }) => {
  const [isVisible, setIsVisible] = useState({});
  const heroRef = useRef(null);
  const featuresRef = useRef(null);
  const benefitsRef = useRef(null);
  const statsRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
        }
      });
    }, observerOptions);

    [heroRef, featuresRef, benefitsRef, statsRef].forEach(ref => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: '⚠️',
      title: 'Get Timely Alerts',
      description: 'Real-time safety alerts and emergency notifications. Stay informed about what matters most in your neighborhood, from weather warnings to community emergencies.'
    },
    {
      icon: '🏘️',
      title: 'Build a Stronger Community',
      description: 'Connect with neighbors, organize events, and collaborate on community initiatives. Foster relationships and create a sense of belonging in your area.'
    },
    {
      icon: '🤝',
      title: 'Connect Without Barriers',
      description: 'Location-based posts keep you connected to your immediate surroundings. Share updates, ask questions, and engage with those closest to you.'
    },
    {
      icon: '📍',
      title: 'Location-Based Posts',
      description: 'Share updates, questions, and events within a specific radius. Connect with neighbors and discover what\'s happening around you right now.'
    },
    {
      icon: '💬',
      title: 'Direct Messaging',
      description: 'Connect privately with neighbors, organize community initiatives, and build meaningful relationships within your neighborhood.'
    },
    {
      icon: '📸',
      title: 'Photo & Video Sharing',
      description: 'Share moments, document events, and showcase your neighborhood through photos and videos that bring your community to life.'
    }
  ];

  const benefits = [
    {
      title: 'Stay Connected',
      description: 'Never miss important community updates, events, or neighborhood discussions. Be part of a vibrant, engaged community.',
      icon: '🤝'
    },
    {
      title: 'Authentic & Local',
      description: 'Real stories from real neighbors. No algorithms, no fake news—just genuine community conversations and local updates.',
      icon: '✨'
    },
    {
      title: 'Easy to Use',
      description: 'Intuitive design makes it simple to post, discover, and engage with your community. Join in seconds, connect instantly.',
      icon: '📱'
    },
    {
      title: 'Privacy Focused',
      description: 'Your data stays yours. Share only what you want, control your visibility, and connect on your terms with privacy protection.',
      icon: '🔒'
    }
  ];

  const stats = [
    { number: '100K+', label: 'Active Users' },
    { number: '500+', label: 'Communities' },
    { number: '1M+', label: 'Posts Shared' },
    { number: '4.8★', label: 'App Rating' }
  ];

  return (
    <div className="mobile-app-landing">
      <Toolbar toggleTheme={toggleTheme} theme={theme} activeSection="mobile-app" />

      {/* Hero Section */}
      <section 
        id="hero" 
        ref={heroRef} 
        className={`mobile-hero ${isVisible.hero ? 'visible' : ''}`}
      >
        <div className="hero-background">
          <div className="hero-orb hero-orb-1"></div>
          <div className="hero-orb hero-orb-2"></div>
          <div className="hero-orb hero-orb-3"></div>
        </div>
        
        <div className="hero-content">
          <div className="hero-badge">
            <span className="badge-icon">📱</span>
            <span className="badge-text">ZoneZapp Mobile App</span>
          </div>
          
          <h1 className="hero-title">
            Neighborhood Watch<br />
            <span className="gradient-text">and Alert Right Now.</span>
          </h1>
          
          <p className="hero-description">
            ZoneZapp connects you to your local community – not the noise of the internet.
            Get real-time safety alerts, monitor neighborhood activity, ask questions,
            and share what's happening around you. Perfect for residents who want
            authentic, on-the-ground updates right now.
          </p>

          <div className="hero-cta">
            <a href="https://apps.apple.com/app/zonezapp/id6593563694" className="cta-button primary" target="_blank" rel="noopener noreferrer">
              <i className="fab fa-apple"></i>
              Download for iOS
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.zonezapp&hl=en" className="cta-button secondary" target="_blank" rel="noopener noreferrer">
              <i className="fab fa-google-play"></i>
              Download for Android
            </a>
          </div>

          <div className="hero-trust">
            <div className="trust-badge">
              <i className="fas fa-shield-alt"></i>
              <span>100% Free & Safe</span>
            </div>
            <div className="trust-badge">
              <i className="fas fa-users"></i>
              <span>100K+ Users</span>
            </div>
            <div className="trust-badge">
              <i className="fas fa-star"></i>
              <span>4.8 Rating</span>
            </div>
          </div>
        </div>

        <div className="hero-phone-mockup">
          <div className="phone-frame">
            <div className="phone-screen">
              <div className="mockup-content">
                <div className="mockup-header">
                  <div className="mockup-location">
                    <i className="fas fa-map-marker-alt"></i>
                    Your Neighborhood
                  </div>
                </div>
                <div className="mockup-posts">
                  <div className="mockup-post">
                    <div className="post-avatar"></div>
                    <div className="post-content">
                      <div className="post-header"></div>
                      <div className="post-text"></div>
                    </div>
                  </div>
                  <div className="mockup-post">
                    <div className="post-avatar"></div>
                    <div className="post-content">
                      <div className="post-header"></div>
                      <div className="post-text"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section 
        id="features" 
        ref={featuresRef} 
        className={`mobile-features ${isVisible.features ? 'visible' : ''}`}
      >
        <div className="features-container">
          <div className="section-header">
            <h2 className="section-title">Everything You Need</h2>
            <p className="section-subtitle">
              Powerful features designed to keep you connected and informed about your community
            </p>
          </div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section 
        id="stats" 
        ref={statsRef} 
        className={`mobile-stats ${isVisible.stats ? 'visible' : ''}`}
      >
        <div className="stats-container">
          {stats.map((stat, index) => (
            <div key={index} className="stat-item" style={{ animationDelay: `${index * 0.1}s` }}>
              <div className="stat-number">{stat.number}</div>
              <div className="stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits Section */}
      <section 
        id="benefits" 
        ref={benefitsRef} 
        className={`mobile-benefits ${isVisible.benefits ? 'visible' : ''}`}
      >
        <div className="benefits-container">
          <div className="section-header">
            <h2 className="section-title">Why Choose ZoneZapp?</h2>
            <p className="section-subtitle">
              More than just another app—a movement to bring communities together
            </p>
          </div>

          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="benefit-icon">{benefit.icon}</div>
                <h3 className="benefit-title">{benefit.title}</h3>
                <p className="benefit-description">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Download CTA Section */}
      <section className="mobile-download-cta">
        <div className="download-cta-content">
          <h2 className="cta-title">Ready to Connect with Your Community?</h2>
          <p className="cta-description">
            Download ZoneZapp today and join thousands of neighbors staying informed, 
            engaged, and connected. It's completely free!
          </p>

          <div className="download-buttons">
            <a href="https://apps.apple.com/app/zonezapp/id6593563694" className="download-btn ios" target="_blank" rel="noopener noreferrer">
              <i className="fab fa-apple"></i>
              <div className="btn-text">
                <span className="btn-small">Download on the</span>
                <span className="btn-large">App Store</span>
              </div>
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.zonezapp&hl=en" className="download-btn android" target="_blank" rel="noopener noreferrer">
              <i className="fab fa-google-play"></i>
              <div className="btn-text">
                <span className="btn-small">Get it on</span>
                <span className="btn-large">Google Play</span>
              </div>
            </a>
          </div>

          <div className="cta-note">
            <i className="fas fa-info-circle"></i>
            Available for iOS 12+ and Android 8.0+
          </div>
        </div>
      </section>

      <TopFooter />
      <BottomFooter />
    </div>
  );
};

export default MobileAppLanding;
